import { TicketPercent } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatDateTime } from '@/shared/lib/format';

import { useCustomerCoupons } from '../api/coupons.queries';
import { COUPON_STATUS, formatCouponScope, formatCouponValue } from '../lib/coupon-format';

/** A customer's personal and points coupons on the customer page (coupons.read). */
export function CustomerCouponsSection({
  customerId,
  customerName,
  timeZone,
}: {
  customerId: number;
  customerName: string;
  /** The customer's market zone. */
  timeZone: string;
}) {
  const coupons = useCustomerCoupons(customerId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Coupons</CardTitle>
        <CardDescription className="text-xs">
          Coupons for {customerName} only — given by staff or bought with points. Public coupons are
          not listed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {coupons.isLoading ? (
          <Skeleton className="h-20" />
        ) : coupons.error || !coupons.data ? (
          <ErrorState error={coupons.error} onRetry={() => void coupons.refetch()} />
        ) : coupons.data.length === 0 ? (
          <EmptyState
            icon={TicketPercent}
            title="No personal coupons"
            description="This customer has none."
          />
        ) : (
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Brands</TableHead>
                <TableHead>Ends</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coupons.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono">{c.code}</TableCell>
                  <TableCell>
                    {c.kind === 'Points'
                      ? `${(c.pointsSpent ?? 0).toLocaleString()} points`
                      : 'Staff'}
                  </TableCell>
                  <TableCell className="tabular-nums">{formatCouponValue(c, c.currency)}</TableCell>
                  <TableCell>{formatCouponScope(c)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(c.validTo, timeZone)}
                  </TableCell>
                  <TableCell>
                    {c.redeemedCount > 0 ? (
                      <StatusBadge variant="success">Used</StatusBadge>
                    ) : (
                      <StatusBadge variant={COUPON_STATUS[c.status].variant}>
                        {COUPON_STATUS[c.status].label}
                      </StatusBadge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
